import { Typography, theme } from "antd";
import { FileText, Landmark, Table2 } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { ICitationBlock, ICitationSource } from "./chat-block.types";

const { Text } = Typography;

type Props = {
	block: ICitationBlock;
};

function sourceIcon(kind: ICitationSource["kind"]): ReactNode {
	switch (kind) {
		case "ledger":
			return <Table2 size={13} />;
		case "bank":
			return <Landmark size={13} />;
		default:
			return <FileText size={13} />;
	}
}

// Provenance chips. When a source is drillable (ref present) the chip is a
// focusable button that navigates to the underlying rows — NOT a web URL. When
// it isn't (e.g. a knowledge-base document) it renders as a plain, non-clickable
// indicator. Keyboard-focusable buttons, not divs.
export function CitationBlock({ block }: Props) {
	const { token } = theme.useToken();
	const navigate = useNavigate();

	if (block.sources.length === 0) return null;

	const chipStyle: CSSProperties = {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		fontSize: 12,
		fontWeight: 600,
		color: token.colorTextSecondary,
		background: token.colorBgContainer,
		border: `1px solid ${token.colorBorderSecondary}`,
		borderRadius: 999,
		padding: "3px 10px",
	};

	return (
		<div
			style={{
				display: "flex",
				flexWrap: "wrap",
				gap: 7,
				alignItems: "center",
			}}
		>
			<Text type="secondary" style={{ fontSize: 11.5, fontWeight: 600 }}>
				Sources
			</Text>
			{block.sources.map((source) => {
				const key = `${source.kind}-${source.label}`;
				const content = (
					<>
						{sourceIcon(source.kind)}
						<span>{source.label}</span>
						{typeof source.count === "number" ? (
							<span style={{ color: token.colorTextTertiary, fontWeight: 500 }}>
								· {source.count}
							</span>
						) : null}
					</>
				);
				const ref = source.ref;
				return ref ? (
					<button
						key={key}
						type="button"
						onClick={() => navigate(ref.route)}
						style={{ ...chipStyle, cursor: "pointer" }}
					>
						{content}
					</button>
				) : (
					<span key={key} style={chipStyle}>
						{content}
					</span>
				);
			})}
		</div>
	);
}
